import { store } from "../redux/root";
import {
	ActionType,
	LogStatus,
	ObjectType,
	SourceType,
} from "../redux/types/common.types";
import { errorlogpayload } from "../redux/types/upload.types";
import { errorlogItems } from "./api.services";
// Note: Import your api function and interface from their correct file paths

export type ClientLogLevel = "error" | "warn" | "info";

export interface ClientLogRequest {
	level: ClientLogLevel;
	event: string;
	message: string;
	error?: unknown;
	context?: Record<string, unknown>;
}

interface SerializableError {
	name?: string;
	message?: string;
	stack?: string;
	code?: string | number;
	status?: number;
	response?: unknown;
	config?: any;
	request?: any;
}

const toSerializableError = (error: unknown): SerializableError | undefined => {
	if (!error) return undefined;

	if (error instanceof Error) {
		const errorWithExtras = error as Error & {
			code?: string | number;
			status?: number;
			response?: any; // Axios response
			config?: any;
		};

		// WAF FIX 1: Never send the full response object.
		// Extract only the status and a truncated piece of the data.
		let safeResponse = undefined;
		if (errorWithExtras.response) {
			const responseData = errorWithExtras.response.data;
			safeResponse = {
				status: errorWithExtras.response.status,
				// If it's HTML or a huge string, slice it to avoid WAF XSS/Size triggers
				data:
					typeof responseData === "string"
						? `${responseData.slice(0, 250)}...`
						: responseData,
			};
		}

		let safeRequest = undefined;
		if (errorWithExtras?.config) {
			let requestBody = errorWithExtras.config.data;

			// Axios usually stringifies the request data. Try to parse it for cleaner logging.
			try {
				if (typeof requestBody === "string") {
					requestBody = JSON.parse(requestBody);
				}
			} catch (e) {
				// If it fails to parse, leave it as a string
			}

			safeRequest = {
				url: errorWithExtras.config.url,
				method: errorWithExtras.config.method,
				// Apply the same WAF safety to the request body
				body:
					typeof requestBody === "string"
						? `${requestBody.slice(0, 250)}...[truncated]`
						: requestBody,
			};
		}

		return {
			name: errorWithExtras.name,
			message: errorWithExtras.message,
			code: errorWithExtras.code || errorWithExtras.status,
			response: safeResponse, // Send the safe, tiny version
			request: safeRequest,
		};
	}

	if (typeof error === "object") {
		// Strip out React/DOM events if they accidentally got passed
		if ("preventDefault" in (error as any))
			return { message: "DOM Event Error" };
		return error as SerializableError;
	}

	return { message: String(error).slice(0, 500) };
};

const safeStringify = (value: unknown) => {
	const seen = new WeakSet<object>();

	return JSON.stringify(value, (_key, currentValue) => {
		if (typeof currentValue === "object" && currentValue !== null) {
			if (seen.has(currentValue)) {
				return "[Circular]";
			}
			seen.add(currentValue);
		}

		// WAF FIX 2: Reduce string truncation from 1000 to 250 characters.
		// 1000 chars across multiple keys can easily breach WAF limits.
		if (typeof currentValue === "string" && currentValue.length > 250) {
			return `${currentValue.slice(0, 250)}...[truncated]`;
		}

		return currentValue;
	});
};

export const logClientError = async ({
	level,
	event,
	message,
	error,
	context,
}: ClientLogRequest): Promise<void> => {
	try {
		const currentOrderId = store.getState().order?.orderId || 0;
		const mappedAction = ActionType[event as keyof typeof ActionType] || 0;

		const mappedStatus =
			level === "error"
				? LogStatus.ERROR
				: level === "warn"
					? LogStatus.WARN
					: LogStatus.INFO;

		const extractedFilename = (context?.fileName || context?.filename) as
			| string
			| undefined;

		// WAF FIX 3: Extract the path as a string instead of assigning the whole context object
		const extractedResourcePath = (context?.s3path ||
			context?.filePath ||
			context?.path ||
			"unknown") as string;

		// Bundle a much leaner payload
		const detailedErrorPayload = {
			err: toSerializableError(error),
			ctx: context,
			path: window.location.pathname,
			// Removed userAgent: userAgents can contain strange characters that trip WAFs
		};

		const stringifiedPayload = safeStringify(detailedErrorPayload);

		const body: errorlogpayload = {
			orderid: Number(currentOrderId),
			objectType: ObjectType.DROPFILE,
			sourceType: SourceType.WEB,
			actionType: mappedAction,
			status: mappedStatus,
			resourceName: extractedFilename,
			resourcePath: context, // Fixed string assignment

			errorDetail:
				error instanceof Error ? error.message : String(message).slice(0, 100),
			errorCode: context?.statusCode?.toString() || "unknown",
			payload: stringifiedPayload,
			clientDateTime: new Date().toISOString(),
		};

		await errorlogItems(body);
	} catch (logError) {
		console.warn("Failed to push client log to server via Axios", logError);
	}
};
